/**
 * Push Notifications Service
 * Сервис для управления push-уведомлениями через Firebase Cloud Messaging
 */

import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { getFirebaseMessaging, VAPID_KEY } from './config';

/**
 * Запрашивает разрешение на отправку уведомлений
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Получает FCM токен для устройства
 * @returns FCM токен или null в случае ошибки
 */
export async function getFCMToken(): Promise<string | null> {
  try {
    const messaging = getFirebaseMessaging();
    
    if (!messaging) {
      return null;
    }

    // Не запрашиваем разрешение автоматически — только проверяем текущий статус
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return null;
    }

    // Получаем токен
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
}

/**
 * Подписывается на получение push-уведомлений
 * @param onMessageCallback Callback для обработки входящих сообщений
 */
export function subscribeToMessages(
  onMessageCallback: (payload: MessagePayload) => void
): (() => void) | null {
  try {
    const messaging = getFirebaseMessaging();
    
    if (!messaging) {
      return null;
    }

    // Подписываемся на сообщения (когда приложение открыто)
    const unsubscribe = onMessage(messaging, (payload) => {
      onMessageCallback(payload);
    });

    return unsubscribe;
  } catch (error) {
    return null;
  }
}

/**
 * Показывает локальное уведомление
 */
export function showLocalNotification(title: string, options?: NotificationOptions) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      icon: '/img/icons/web-app-manifest-192x192.png',
      badge: '/img/icons/favicon-96x96.png',
      ...options
    });
  }
}

/**
 * Проверяет, поддерживает ли браузер push-уведомления
 */
export function isPushNotificationSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Получает текущий статус разрешения на уведомления
 */
export function getNotificationPermission(): NotificationPermission {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'denied';
}

